import React, { useEffect } from 'react'
import styled from 'styled-components'
import { numberise } from '@jarvis.oddle/core/tools/style'
import {
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import {
  getEmailError,
  getFirstNameError,
  getLastNameError
} from '@jarvis.oddle/core/tools/validation'
import modalController from 'src/decorators/ModalController'
import Flex from '@jarvis.oddle/core/components/Flex'
import Form, {
  makeFormField,
  withFormContext
} from '@jarvis.oddle/core/components/Form'
import Input from '@jarvis.oddle/core/components/Input'
import { Column, Grid, Row } from '@jarvis.oddle/core/components/Grid'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import useTracker from 'src/hooks/useTracker'
import { DesktopOnly, MobileOnly } from 'src/components/core/Responsive'
import { newsletterSignupAction } from 'src/redux/newsletter'
import useAppDispatch from '../../../hooks/useAppDispatcher'

const StyledSubmitButton = styled(Form.SubmitButton)`
  width: 100%;
  height: 100%;
`

const StyledForm = styled(Form)`
  align-self: stretch;
  flex-shrink: 0;
`

const EmailErrorMessage = withFormContext(({ form: { errors, touched } }) =>
  errors.email && touched.email ? (
    <Form.FieldError>{errors.email}</Form.FieldError>
  ) : null
)

const FormInput = makeFormField(({ fieldError, fieldTouched, ...props }) => (
  <Flex width='100%' marginBottom={marginSizes.medium}>
    <Input {...props} withError={fieldError && fieldTouched} controlled />
  </Flex>
))

const onNewsletterSignupSuccess = ({ firstName }) => {
  modalController.closeModal('MarketingPopupModal', () => {
    modalController.showModal('NewsletterSignupModal', { firstName })
  })
}

const gutterSize = numberise(padding[paddingSizes.small])

interface NewsletterSignupFormProps {
  previewMode: boolean
  requestSubscriberName: boolean
}

const NewsletterSignupForm = ({
  previewMode,
  requestSubscriberName,
  ...props
}: NewsletterSignupFormProps): JSX.Element => {
  const dispatch = useAppDispatch()
  const { t } = React.useContext(TranslationContext)
  const { notifyNewsletterCompleteRegistration, notifyNewsletterLead } =
    useTracker()
  useEffect(() => {
    if (!previewMode) {
      notifyNewsletterLead()
    }
  }, [notifyNewsletterLead, previewMode])

  const handleSubmit = async (
    { email, firstName, lastName },
    { setFieldError, setSubmitting }
  ) => {
    if (previewMode) {
      onNewsletterSignupSuccess({ firstName })
      return
    }
    const response = await dispatch(
      newsletterSignupAction({
        email,
        firstName,
        lastName
      })
    )
    if (
      response.meta.requestStatus === 'fulfilled' &&
      response.payload.data === 'Email subscribed'
    ) {
      notifyNewsletterCompleteRegistration({
        email,
        firstName,
        lastName
      })
      onNewsletterSignupSuccess({ firstName })
    } else {
      setFieldError(
        'email',
        t('Looks like something went wrong, please try again later.')
      )
      setSubmitting(false)
    }
  }

  const handleValidate = ({
    email,
    firstName,
    lastName
  }: {
    email: string
    firstName: string
    lastName: string
  }) => {
    const errors: {
      email?: string
      firstName?: string
      lastName?: string
    } = {}

    const emailError = getEmailError(t, email)
    if (emailError) errors.email = emailError

    if (requestSubscriberName) {
      const firstNameError = getFirstNameError(t, firstName)
      const lastNameError = getLastNameError(t, lastName)
      if (firstNameError) errors.firstName = firstNameError
      if (lastNameError) errors.lastName = lastNameError
    }

    return errors
  }

  return (
    <StyledForm
      {...props}
      initialValues={{ firstName: '', lastName: '', email: '' }}
      onSubmit={handleSubmit}
      onValidate={handleValidate}
    >
      <Grid>
        <Row gutterSize={gutterSize}>
          {requestSubscriberName && (
            <React.Fragment>
              <Column gutterSize={gutterSize} sm={6}>
                <Form.Input name='firstName' label={t('First Name')} />
              </Column>
              <Column gutterSize={gutterSize} sm={6}>
                <Form.Input name='lastName' label={t('Last Name')} />
              </Column>
            </React.Fragment>
          )}
          <Column gutterSize={gutterSize} sm={12} lg={8}>
            <FormInput name='email' label={t('Email Address')} />
            <MobileOnly>
              <EmailErrorMessage />
            </MobileOnly>
          </Column>
          <Column gutterSize={gutterSize} sm={12} lg={4}>
            <Flex height='100%' paddingBottom={paddingSizes.medium}>
              <StyledSubmitButton>{t('Signup')}</StyledSubmitButton>
            </Flex>
          </Column>
        </Row>
      </Grid>
      <DesktopOnly>
        <EmailErrorMessage />
      </DesktopOnly>
    </StyledForm>
  )
}

export default NewsletterSignupForm
